import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { laneApi, LaneTaskRow, LaneStatusCounts, WorklistCounts } from '../../api/laneApi';
import { unifiedTasksApi, UnifiedTask } from '../../api/unifiedTasks';
import { useAuth } from '../../context/AuthContext';
import { ToastProvider } from '../../components/curation/ToastContext';
import { FinalResponseSupportProvider } from '../../components/curation/finalResponseSupport';
import { OneEmailModal } from '../../components/curation/teach/OneEmailModal';
import { Claim } from '../../api/claims';
import { Button } from '../../components/ui';
import { LaneShell, LaneListSlotProps, LaneDetailSlotProps } from './LaneShell';
import {
  filtersFromSearchParams, applySort, applyPage, applyFilter, applySelectedTask, RECEIVED_FROM_OFF,
} from './laneUrlState';
import { toggleSelect } from './laneSelection';
import { agingLevel, agingLabel, parseBackendTimestamp, formatShortDate } from './aging';
import { RECEIVED_FROM_1_JULY_2026 } from '../../constants/receivedFrom';
import { ActorLabel } from './actorLabel';
import { TaskDetailPane } from './TaskDetailPane';
import { ClaimPickerModal } from './ClaimPickerModal';
import { buildBulkAssignConfirmation, recordClaimAttach } from './claimRefMatch';
import { isLaneModalOpen } from './laneModals';
import { MemberPickerModal } from './MemberPickerModal';
import { ArchiveReasonModal } from './ArchiveReasonModal';
import { ForwardComposer } from './ForwardComposer';
import { replacementRunLabel } from '../../components/curation/automation/AutomationRunDrawer';
import { FINAL_RESPONSE_CHECK_WORDS } from '../../components/curation/copy';
import { WorklistChips } from './WorklistChips';
import { FinalResponseCheckModal } from './FinalResponseCheckModal';
import {
  StanceChoice, WORKLIST_CHIPS, WorklistChoice, canMakeExample, claimLink, dueText, dueTone, finalResponseMarker,
  readStoredWorklist, storeWorklist, teachPreset, worklistsSupported,
} from './worklists';
import './lanes.css';

/**
 * Lender lane body — Tasks Rebuild Task 10. Per-email rows (validated
 * mockup, spec.md Part 2): NOT grouped like the Client lane's accordion --
 * every row is its own email. Adds two things ClientLane (Task 9) didn't
 * have, per the Task 10 brief:
 *   - a requestId/latest-wins guard on the list fetch (ClientLane's fetches
 *     have no such guard; the legacy Tasks.tsx loadTasks() does -- see
 *     src/pages/Tasks.tsx's latestRequestIdRef), so a slow stale response
 *     for an old page/sort/filter can never clobber a newer one, and
 *   - j/k keyboard navigation through the page's rows.
 *
 * The since-01/07/2026 default chip and the sort control are owned locally
 * here (not laneUrlState.ts) via the shared `receivedFrom`/`sortBy`/
 * `sortOrder` fields that file already defines -- same treatment
 * ClientLane gave its Client-lane-only `group`/`flat` params.
 */

const DISPLAY_RECEIVED_FROM = RECEIVED_FROM_1_JULY_2026.split('-').reverse().join('/'); // "01/07/2026" -- never new Date() (UK DD/MM gotcha)

/** Matches the FRFL "AN:|<digits>" agreement-number reference embedded in
 * some lender subjects (see caf_task_classifier.py's `_AN_AGREEMENT_RE` /
 * `project_caf_an_reference_matching.md`) so it can be visibly called out
 * in the row instead of disappearing into the rest of the subject text. */
const AN_REF_RE = /\bAN:\|?\d{6,}\b/i;

function renderSubjectWithRef(subject: string | null): React.ReactNode {
  if (!subject) return '(no subject)';
  const match = subject.match(AN_REF_RE);
  if (!match || match.index === undefined) return subject;
  const before = subject.slice(0, match.index);
  const ref = match[0];
  const after = subject.slice(match.index + ref.length);
  return (
    <>
      {before}
      <span className="lender-lane__an-ref">{ref}</span>
      {after}
    </>
  );
}

/** DD/MM display per the validated mockup. Deliberately not a full
 * date/year -- the aging badge (colour + title tooltip) carries the "how
 * stale is this" signal; this is just "which day did it land". Derived
 * from `parseBackendTimestamp`'s already-correct UTC instant, so this is a
 * local-time *display* step, not a repeat of the naive-timestamp parsing
 * gotcha aging.ts documents. */
function formatReceivedDDMM(iso: string | null): string {
  if (!iso) return '—';
  const d = parseBackendTimestamp(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

type SortChoice = 'receivedAsc' | 'receivedDesc' | 'createdDesc';

function sortChoiceToParams(choice: SortChoice): { sortBy: string; sortOrder: 'asc' | 'desc' } {
  switch (choice) {
    case 'receivedDesc': return { sortBy: 'receivedDate', sortOrder: 'desc' };
    case 'createdDesc': return { sortBy: 'createdAt', sortOrder: 'desc' };
    case 'receivedAsc':
    default: return { sortBy: 'receivedDate', sortOrder: 'asc' };
  }
}

function paramsToSortChoice(sortBy: string | null, sortOrder: 'asc' | 'desc' | null): SortChoice {
  if (sortBy === 'createdAt') return 'createdDesc';
  if (sortBy === 'receivedDate' && sortOrder === 'desc') return 'receivedDesc';
  return 'receivedAsc';
}

type BulkAction = 'complete' | 'assign' | 'archive' | 'assignMember';

interface BulkProgress {
  action: BulkAction;
  done: number;
  total: number;
  failed: number;
}

// Present-continuous verb for the in-flight progress line ("Completing
// 2/5"), and the past-participle suffix for the result banner ("2
// completed" / "1/2 completed — 1 failed") -- the 'complete' wording is
// pinned to match pre-existing LenderLane.test.tsx assertions verbatim;
// assign/archive/assignMember follow the same shape.
const BULK_ACTION_PROGRESS_VERB: Record<BulkAction, string> = {
  complete: 'Completing',
  assign: 'Assigning',
  archive: 'Archiving',
  assignMember: 'Assigning',
};
const BULK_ACTION_RESULT_SUFFIX: Record<BulkAction, string> = {
  complete: 'completed',
  assign: 'assigned',
  archive: 'archived',
  assignMember: 'assigned',
};

/** Bridges LaneShell's internal `refreshToken` (only visible inside the
 * render-prop slots) up to this component's own fetch effect -- same
 * pattern as ClientLane's RefreshBridge. */
const RefreshBridge: React.FC<{ token: number; onChange: (t: number) => void }> = ({ token, onChange }) => {
  useEffect(() => { onChange(token); }, [token, onChange]);
  return null;
};

export const LenderLane: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearchParams(searchParams);

  // Default sort per the brief: received asc when Pending; Completed "sorts
  // archivedAt-equivalent via createdAt" (these active-table completed rows
  // have no archivedAt -- createdAt is the closest available proxy; the
  // archived:yes operator that actually reaches archived_email_tasks is
  // Task 13 scope). Only takes effect until the user picks a sort
  // explicitly, at which point the URL's sortBy/sortOrder win and persist.
  const defaultSortChoice: SortChoice = filters.tab === 'completed' ? 'createdDesc' : 'receivedAsc';
  const sortChoice: SortChoice = (filters.sortBy || filters.sortOrder)
    ? paramsToSortChoice(filters.sortBy, filters.sortOrder)
    : defaultSortChoice;

  const receivedFromActive = !!filters.receivedFrom;

  // Task 13: the omnibox's since: operator can set `receivedFrom` to a date
  // other than the 1 July default (see Omnibox.tsx applying its `since`
  // chip through this exact same URL field). The active-chip label must
  // reflect whichever date is actually in effect, not always claim "01/07/
  // 2026" -- the re-add button below is the one place that's still
  // correctly hardcoded, since it only ever re-adds the specific 1 July
  // default, never an arbitrary omnibox-set date.
  const receivedFromDisplay = filters.receivedFrom
    ? filters.receivedFrom.split('-').reverse().join('/')
    : DISPLAY_RECEIVED_FROM;

  // Since-01/07/2026 chip: ticked by default, but the URL -- not a ref --
  // is the single source of truth for whether it's been touched, because a
  // ref is per-mount and this lane remounts on ordinary SPA tab navigation
  // (LaneShell/App.tsx routing unmounts LenderLane when you leave /tasks/
  // lender and remounts it fresh when you come back). A ref-based "have we
  // defaulted yet" guard can't survive that: it resets to false on every
  // remount, and if dismissal had merely deleted the `receivedFrom` param
  // (indistinguishable from "never touched"), the very next remount would
  // silently re-add the filter the user just dismissed -- the exact "chip
  // comes back" bug this rebuild exists to fix.
  //
  // So `receivedFrom` carries three URL states instead of two (see
  // laneUrlState.ts):
  //   - absent                          -> never touched; apply the default
  //   - '=off' (RECEIVED_FROM_OFF)      -> explicitly dismissed; stays off,
  //                                        chip renders re-addable
  //   - '=<date>'                       -> active filter
  // Dismiss therefore sets the param to 'off' rather than deleting it, so
  // "dismissed" is durably recorded in the URL and survives any remount.
  // filtersFromSearchParams() translates 'off' back to `receivedFrom: null`
  // (via `receivedFromDismissed` instead) so the API layer below can never
  // forward `receivedFrom=off` to the backend.
  //
  // Toggling this chip swaps the result set out from under any bulk
  // selection, so both directions clear `checkedIds`/`bulkResult` the same
  // way a search-query change does -- see the effect below keyed on
  // `filters.receivedFrom`/`filters.q`.
  const dismissReceivedFrom = () => setSearchParams((prev) => applyFilter(prev, 'receivedFrom', RECEIVED_FROM_OFF));
  const addReceivedFrom = () => setSearchParams(
    (prev) => applyFilter(prev, 'receivedFrom', RECEIVED_FROM_1_JULY_2026),
  );

  const setSortChoice = (choice: SortChoice) => {
    const { sortBy, sortOrder } = sortChoiceToParams(choice);
    setSearchParams((prev) => applyPage(applySort(prev, sortBy, sortOrder), 1));
  };

  const setSelectedTaskId = (taskId: string | null) => setSearchParams((prev) => applySelectedTask(prev, taskId));

  const [shellRefreshToken, setShellRefreshToken] = useState(0);
  // Final response / Not sure actions (spec §4.12, R10, R16): only the people
  // who can save examples (admin, corpus-curator -- R6) see them; the
  // adjudication endpoints refuse everyone else anyway.
  const { isAdmin, hasRole } = useAuth();
  const canTeach = isAdmin || hasRole('corpus-curator');
  const [frCheck, setFrCheck] = useState<{ row: LaneTaskRow; isFinalResponse: boolean } | null>(null);
  const [teach, setTeach] = useState<{ row: LaneTaskRow; stance: StanceChoice | null } | null>(null);

  // AI status filter (2026-08-10): staff working the lane can't tell which
  // rows the AI has already been through, so they pick up work the AI was
  // about to auto-complete while genuine escalations wait behind it.
  // Default 'all' -- the queue must not silently change for anyone; local
  // (not URL) state, per the brief -- fetchRows below turns 'all' into
  // `undefined` on the wire, so an untouched control produces a
  // byte-identical request to before this feature existed. Changing it
  // resets to page 1 the same way the URL-based filters' applyFilter does,
  // since it swaps the underlying result set the same way they do.
  const [aiProcessed, setAiProcessed] = useState<'all' | 'processed' | 'unprocessed'>('all');
  const handleAiProcessedChange = (value: 'all' | 'processed' | 'unprocessed') => {
    setAiProcessed(value);
    setSearchParams((prev) => applyPage(prev, 1));
  };

  // Lender task lists (2026-10-08 worklists, spec §4.12, R17). Replaces the
  // old Final response yes/no filter. Local state, remembered per viewer in
  // localStorage (a convenience only; never URL state, never shared). Lists
  // exist only for live pending lender emails -- completed and archived
  // tasks leave them -- so the list applies on Pending and never on archived.
  // `worklistSupported` is null until the first response: an older backend
  // (no counts) flips it to false, which makes the lane behave exactly as it
  // did before the lists, whatever chip was remembered.
  const [worklist, setWorklist] = useState<WorklistChoice>(() => readStoredWorklist());
  const [worklistCounts, setWorklistCounts] = useState<WorklistCounts | undefined>(undefined);
  const [worklistSupported, setWorklistSupported] = useState<boolean | null>(null);
  const worklistApplies = filters.tab === 'pending' && !filters.archived;
  const activeWorklist: WorklistChoice = worklistApplies && worklistSupported !== false ? worklist : 'all';
  const activeWorklistLabel = WORKLIST_CHIPS.find((chip) => chip.key === activeWorklist)?.label ?? '';
  const showWorklistChips = worklistApplies && worklistSupported === true;
  // Needs a reply is sorted by reply-by date on the server (R14), so the
  // lane sends no sort of its own for it.
  const sortedByReplyBy = activeWorklist === 'needs_reply';
  const handleWorklistChange = (choice: WorklistChoice) => {
    setWorklist(choice);
    storeWorklist(choice);
    setSearchParams((prev) => applyPage(prev, 1));
  };
  const [workflowFilter, setWorkflowFilter] = useState<'all' | 'only' | 'hide'>('all');
  const [workflowSupported, setWorkflowSupported] = useState(false);
  const handleWorkflowFilter = (value: 'all' | 'only' | 'hide') => {
    setWorkflowFilter(value);
    setSearchParams((prev) => applyPage(prev, 1));
  };

  const [rows, setRows] = useState<LaneTaskRow[]>([]);
  const [total, setTotal] = useState(0);
  // Task 11: pending/completed split for the toggle buttons -- see
  // LaneShell's `statusCounts` prop doc comment. `undefined` until the
  // first response lands (or forever, against a stale backend that omits
  // the field), so the buttons render bare "Pending"/"Completed" rather
  // than a fabricated "(0)".
  const [statusCounts, setStatusCounts] = useState<LaneStatusCounts | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowsById, setRowsById] = useState<Record<string, LaneTaskRow>>({});

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);
  // Outlives `bulkProgress`/`checkedIds`: the completion (incl. failure)
  // summary staff need to actually read. `handleBulkComplete` clears
  // `checkedIds` the moment it finishes, and both state updates land in the
  // same React batch -- if the summary were gated on `checkedIds.size > 0`
  // (the way the rest of the bulk bar is), it would vanish in the very same
  // render it was meant to appear in, so a partial-failure "N failed" would
  // never be visible. Rendered as its own dismissible banner outside that
  // gate; auto-cleared on the next selection or filter/query change (see
  // the effect below) so a stale result can't linger into an unrelated
  // future action.
  const [bulkResult, setBulkResult] = useState<{ action: BulkAction; total: number; failed: number } | null>(null);

  // Bulk assign/forward/archive dialogs (Task 12) -- see the shared
  // ClaimPickerModal/ArchiveReasonModal/ForwardComposer components' doc
  // comments for why they're extracted rather than built inline here.
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  // Staff assignment restoration (fast-follow) -- same shared
  // MemberPickerModal used by ManualLane/PostLane's bulk bars and
  // TaskDetailPane's single-task control. Button is no longer isAdmin-gated
  // (Item 2, lane-feedback batch 28/07, Ryan decision -- see that file's
  // identical comment on why).
  const [bulkAssignMemberOpen, setBulkAssignMemberOpen] = useState(false);
  const [bulkAssigningMember, setBulkAssigningMember] = useState(false);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkArchiving, setBulkArchiving] = useState(false);
  // Task 12 review finding (Medium): the single-task pane refuses to archive
  // a claim-assigned task (TaskDetailPane's `canArchive` gate) but the bulk
  // path had no equivalent filter, so checking a mix of claim-assigned and
  // unassigned rows and hitting bulk Archive would silently archive rows the
  // single-task UI would never let through. `archivableIds`/
  // `archiveSkippedCount` hold the partition computed at click-time (see
  // `handleBulkArchiveClick`) so the confirm copy and the actual bulk run
  // both work off "no assignedClaimId" rows only; `archiveBlockedMessage`
  // covers the all-assigned case where there's nothing left to archive at
  // all, so the modal never even opens.
  const [archivableIds, setArchivableIds] = useState<string[]>([]);
  const [archiveSkippedCount, setArchiveSkippedCount] = useState(0);
  const [archiveBlockedMessage, setArchiveBlockedMessage] = useState<string | null>(null);
  // Forwarding is fundamentally single-target (EmailComposer needs one
  // subject/body to prefill and one human to write the reply) -- the bulk
  // bar's Forward button only acts when exactly one row is checked; this
  // holds that one task id while its composer is open.
  const [bulkForwardTaskId, setBulkForwardTaskId] = useState<string | null>(null);

  // Latest-wins guard: a fetch whose response lands after a newer one has
  // already fired (e.g. the user changed page/sort/tab again before the
  // first request returned) is dropped instead of overwriting fresher rows.
  // ClientLane (Task 9) doesn't have this; the legacy Tasks.tsx does (see
  // latestRequestIdRef there) -- the Task 10 brief calls out adding it here.
  const requestIdRef = useRef(0);

  const fetchRows = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    const { sortBy, sortOrder } = sortChoiceToParams(sortChoice);
    laneApi.getLaneTasks('lender', {
      statusTab: filters.tab,
      page: filters.page,
      pageSize: filters.pageSize,
      sortBy: sortedByReplyBy ? undefined : sortBy,
      sortOrder: sortedByReplyBy ? undefined : sortOrder,
      // `filters.receivedFrom` rather than the hardcoded July-1 constant --
      // Task 13's omnibox `since:` operator writes an arbitrary date into
      // this same URL field (see Omnibox.tsx), and the fetch must honour
      // whatever date is actually active, not silently coerce every active
      // filter back to the 1 July default.
      receivedFrom: receivedFromActive ? (filters.receivedFrom || undefined) : undefined,
      // #4: until: -> receivedTo. No default-on/dismiss lifecycle like
      // receivedFrom's (there's no "until-from-1-July" concept to default),
      // so this is a plain pass-through of whatever `until` the omnibox
      // wrote into the URL -- combines with an active receivedFrom into a
      // since..until range, and is independently a no-op when
      // filters.until is unset regardless of receivedFromActive.
      receivedTo: filters.until || undefined,
      senderEmail: filters.senderEmail || undefined,
      claim: filters.claim || undefined,
      assigned: filters.assigned || undefined,
      archived: filters.archived || undefined,
      q: filters.q || undefined,
      // 'all' sends nothing -- see the `aiProcessed` state's own comment on
      // why the default must stay byte-identical to a pre-filter request.
      // Also forced to `undefined` whenever `filters.archived` is active,
      // regardless of what `aiProcessed` local state currently holds --
      // the control is hidden on that view (see the toolbar's `!filters.
      // archived` guard) but the state itself is NOT reset by hiding it,
      // so without this explicit override a filter chosen on the live view
      // would silently keep being sent after the user toggles archived on,
      // against data (archived_email_tasks.ai_processed_at pre-2026-07-14)
      // this branch itself has already established isn't trustworthy.
      aiProcessed: (filters.archived || aiProcessed === 'all') ? undefined : aiProcessed,
      // All sends nothing, so an untouched lane makes the same request as
      // before the lists existed. activeWorklist is already All on Completed,
      // on archived and against an older backend.
      worklist: activeWorklist === 'all' ? undefined : activeWorklist,
      workflow: workflowFilter === 'all' ? undefined : workflowFilter,
    })
      .then((res) => {
        if (requestId !== requestIdRef.current) return; // stale response, dropped
        setRows(res.tasks);
        setWorkflowSupported((known) => known || res.tasks.some((row) => Object.prototype.hasOwnProperty.call(row, 'workflow')));
        setTotal(res.total);
        setStatusCounts(res.statusCounts);
        setWorklistCounts(res.worklistCounts ?? undefined);
        setWorklistSupported((known) => known === true || worklistsSupported(res));
        setRowsById((prev) => {
          const next = { ...prev };
          res.tasks.forEach((r) => { next[r.id] = r; });
          return next;
        });
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setError('Failed to load lender emails.');
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.tab, filters.page, filters.pageSize, filters.q, sortChoice, receivedFromActive,
    filters.receivedFrom, filters.until, filters.senderEmail, filters.claim, filters.assigned, filters.archived,
    aiProcessed, workflowFilter, activeWorklist, sortedByReplyBy,
  ]);

  useEffect(() => {
    if (!filters.receivedFrom && !filters.receivedFromDismissed) {
      // Never touched (both this component's URL and `receivedFromDismissed`
      // agree there's no 'off' sentinel either) -- default the filter into
      // the URL and defer to the next render (which re-derives
      // `receivedFromActive` as true) instead of fetching now with it
      // missing. Because this reads straight off `filters`, which is
      // derived fresh from the URL on every render, it behaves identically
      // on first-ever mount and on every remount -- there is no per-mount
      // ref to reset, so a prior dismissal (durably recorded as `off`) is
      // never mistaken for "never touched".
      setSearchParams((prev) => applyFilter(prev, 'receivedFrom', RECEIVED_FROM_1_JULY_2026));
      return;
    }
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRows, shellRefreshToken, filters.receivedFrom, filters.receivedFromDismissed]);

  // The open email follows the list (demo fix, 2026-10-10): once a fresh list
  // no longer has the email that was open in the list before it -- another
  // chip, or a saved verdict that moved it off this list -- the pane closes
  // back to "Select an email" instead of showing an email that isn't there.
  // A linked email this page never listed stays open (the deep-link path).
  const listedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const listedBefore = listedIdsRef.current;
    const listedNow = new Set(rows.map((r) => r.id));
    listedIdsRef.current = listedNow;
    if (filters.selected && listedBefore.has(filters.selected) && !listedNow.has(filters.selected)) {
      setSelectedTaskId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Bulk checkbox selection (and any leftover completion banner) is
  // transient UI state, not URL state (unlike the single detail-pane
  // `selected` param) -- clear both whenever the page/tab changes, the
  // since-01/07/2026 chip is toggled, the search query changes, or any
  // omnibox-driven filter (from/claim/assigned/archived, Task 13) changes,
  // since each of those swaps the underlying result set out from under
  // whatever was selected/reported on.
  useEffect(() => {
    setCheckedIds(new Set());
    setBulkResult(null);
    setArchiveBlockedMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.tab, filters.page, filters.receivedFrom, filters.receivedFromDismissed, filters.q,
    filters.senderEmail, filters.claim, filters.assigned, filters.archived, aiProcessed, workflowFilter, activeWorklist,
  ]);

  // Making a fresh selection also retires any leftover completion banner
  // from a prior bulk action -- it described a result set that no longer
  // matches what's checked now. Same treatment for the archive-blocked
  // message: it described the previous selection's assigned/unassigned mix.
  const toggleChecked = (id: string) => {
    setBulkResult(null);
    setArchiveBlockedMessage(null);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allOnPageChecked = rows.length > 0 && rows.every((r) => checkedIds.has(r.id));
  const toggleAllOnPage = () => {
    setBulkResult(null);
    setArchiveBlockedMessage(null);
    setCheckedIds((prev) => {
      if (allOnPageChecked) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  // Shared per-item-failure-isolated bulk runner (Task 12): every bulk
  // action (complete/assign/archive) iterates its ids sequentially so one
  // failure doesn't abort the rest, tracks live progress, then reports a
  // single `bulkResult` summary that outlives `checkedIds` (see that state's
  // comment above -- same T10 fix pattern extended to the new actions).
  const runBulk = async (action: BulkAction, ids: string[], fn: (id: string) => Promise<any>) => {
    setBulkResult(null);
    setBulkProgress({ action, done: 0, total: ids.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < ids.length; i++) {
      try {
        await fn(ids[i]);
      } catch {
        failed += 1;
      }
      setBulkProgress({ action, done: i + 1, total: ids.length, failed });
    }
    setBulkProgress(null);
    setBulkResult({ action, total: ids.length, failed });
    setCheckedIds(new Set());
    fetchRows();
  };

  // Single-request variant of runBulk (perf fix -- USER REPORT: bulk "Assign
  // to staff" on 67 rows crawled at "Assigning 1/67" because it drove 67
  // sequential POST /ui/team-tasks/bulk-assign calls, one per id, even
  // though that route already accepts the full taskIds array and does one
  // SQL UPDATE for the lot. `fn` here takes the WHOLE id list and resolves
  // to however many the server actually matched/updated (bulk_assign_tasks
  // silently skips ids it can't find rather than erroring -- no per-id
  // success/failure to report, just a partial *count*), so progress is
  // indeterminate (no per-item ticks) then jumps straight to the result
  // banner once the single request settles.
  const runBulkOneShot = async (action: BulkAction, ids: string[], fn: (ids: string[]) => Promise<number>) => {
    setBulkResult(null);
    setBulkProgress({ action, done: 0, total: ids.length, failed: 0 });
    let failed = 0;
    try {
      const succeeded = await fn(ids);
      failed = Math.max(0, ids.length - succeeded);
    } catch {
      failed = ids.length;
    }
    setBulkProgress(null);
    setBulkResult({ action, total: ids.length, failed });
    setCheckedIds(new Set());
    fetchRows();
  };

  const handleBulkComplete = async () => {
    const ids = Array.from(checkedIds);
    if (ids.length === 0) return;
    const plural = ids.length === 1 ? '' : 's';
    // Every id here came from a checkbox on an already-fetched row, so the
    // count being confirmed is always the true, uncapped total being acted
    // on (the discrepancy ClientLane's "true-total copy fix" guards against
    // -- fetched vs. group-authoritative counts -- can't happen for an
    // explicit per-row selection like this).
    const ok = window.confirm(`Complete ${ids.length} email${plural}?`);
    if (!ok) return;
    await runBulk('complete', ids, (id) => unifiedTasksApi.completeTask(id));
  };

  const handleBulkAssignConfirm = async (claim: Claim) => {
    const ids = Array.from(checkedIds);
    setBulkAssignOpen(false);
    if (ids.length === 0) return;
    setBulkAssigning(true);
    await runBulk('assign', ids, async (id) => {
      await unifiedTasksApi.assignTask(id, claim.id, rowsById[id]?.emailType || undefined);
      recordClaimAttach(claim.id, [id]);
    });
    setBulkAssigning(false);
  };

  // Wrong-claim attach guard (2026-09-28): before a bulk assign, count the
  // checked rows whose own reference (AI ref + subject refs, carried on the
  // list row) points at a different claim. Rows with no reference never
  // block. Refs only -- never client names -- in the confirm.
  const getBulkAssignConfirmation = (claim: Claim) =>
    buildBulkAssignConfirmation(claim.id, Array.from(checkedIds), rowsById);

  const handleBulkAssignMemberConfirm = async (member: string | null) => {
    const ids = Array.from(checkedIds);
    setBulkAssignMemberOpen(false);
    if (ids.length === 0) return;
    setBulkAssigningMember(true);
    await runBulkOneShot('assignMember', ids, (allIds) => laneApi.assignMember(allIds, member).then((r) => r.assigned));
    setBulkAssigningMember(false);
  };

  // Partitions the checked rows into archivable (no assignedClaimId) vs
  // skipped (assigned to a claim -- the single-task pane's `canArchive` gate
  // refuses these, so bulk must too). `rowsById` rather than `rows` because
  // every checked id was checked from an already-fetched row, so it's
  // guaranteed present there regardless of which page it came from.
  const partitionArchivable = (ids: string[]) => {
    const archivable = ids.filter((id) => !rowsById[id]?.assignedClaimId);
    return { archivable, skipped: ids.length - archivable.length };
  };

  const handleBulkArchiveClick = () => {
    const ids = Array.from(checkedIds);
    const { archivable, skipped } = partitionArchivable(ids);
    if (archivable.length === 0) {
      setArchiveBlockedMessage("None of the selected tasks can be archived — tasks assigned to a claim can't be archived.");
      return;
    }
    setArchiveBlockedMessage(null);
    setArchivableIds(archivable);
    setArchiveSkippedCount(skipped);
    setBulkArchiveOpen(true);
  };

  const handleBulkArchiveConfirm = async (reason: string) => {
    const ids = archivableIds;
    setBulkArchiveOpen(false);
    if (ids.length === 0) return;
    setBulkArchiving(true);
    await runBulk('archive', ids, (id) => unifiedTasksApi.archiveTask(id, reason));
    setBulkArchiving(false);
  };

  const bulkForwardEligible = checkedIds.size === 1;
  const handleBulkForwardClick = () => {
    if (!bulkForwardEligible) {
      window.alert('Select exactly one email to forward.');
      return;
    }
    setBulkForwardTaskId(Array.from(checkedIds)[0]);
  };

  // j/k triage through the current page's rows, moving the detail-pane
  // selection (the shared `selected` URL param). Ignored while focus is in
  // an input/select (omnibox, sort control, checkboxes shouldn't fight
  // typing/toggling), while no rows are loaded, and while ANY modal is open
  // -- including the detail pane's own claim picker, which this lane's
  // state can't see (wrong-claim attach guard, 2026-09-28).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'j' && e.key !== 'k') return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (isLaneModalOpen()) return;
      if (rows.length === 0) return;
      e.preventDefault();
      const currentIndex = rows.findIndex((r) => r.id === filters.selected);
      let nextIndex: number;
      if (currentIndex === -1) {
        nextIndex = 0;
      } else if (e.key === 'j') {
        nextIndex = Math.min(rows.length - 1, currentIndex + 1);
      } else {
        nextIndex = Math.max(0, currentIndex - 1);
      }
      setSelectedTaskId(rows[nextIndex].id);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters.selected]);

  const renderChips = () => (
    <div className="lender-lane__chips">
      {receivedFromActive ? (
        <span
          className={`lender-lane__chip ${filters.archived ? 'lender-lane__chip--muted' : ''}`}
          title={filters.archived ? "Not applied -- archived browsing doesn't support a received-date filter." : undefined}
        >
          Only since {receivedFromDisplay}
          <button
            type="button"
            className="lender-lane__chip-dismiss"
            aria-label={`Remove the since ${receivedFromDisplay} filter`}
            onClick={dismissReceivedFrom}
          >
            &times;
          </button>
        </span>
      ) : (
        <button type="button" className="lender-lane__chip-readd" onClick={addReceivedFrom}>
          + Only since {DISPLAY_RECEIVED_FROM}
        </button>
      )}
    </div>
  );

  // Demo finding: "for completed emails we're missing a lot of the details
  // of who completed it and when it was completed." Aging (how long a task
  // waited before being actioned) is meaningless once it's done, so the
  // Completed sub-tab and the archived view both swap the aging badge for
  // completed-by/archived-by attribution instead -- archived takes priority
  // over completed when both apply (a row can be completed-then-archived;
  // the archive view is specifically about the archive action).
  const showCompletionInfo = filters.tab === 'completed' || filters.archived;

  // Same union as `showCompletionInfo` above -- provably identical today --
  // but named separately rather than reused, because it carries an
  // unrelated concern: whether a row's `aiProcessed: false` is trustworthy
  // enough to badge "Awaiting AI" (see the badge's own comment below for
  // why completed and archived rows each independently fail that test).
  // Sharing `showCompletionInfo` would leave that coupling implicit --
  // someone changing it for aging/completion-attribution reasons alone
  // would silently change AI-badge behaviour too, with nothing at either
  // call site naming the connection.
  const aiBadgeUntrustworthy = filters.tab === 'completed' || filters.archived;

  const renderList = (slotProps: LaneListSlotProps) => (
      <div className="lender-lane">
        <RefreshBridge token={slotProps.refreshToken} onChange={setShellRefreshToken} />
        {showWorklistChips && (
          <WorklistChips value={worklist} counts={worklistCounts} onChange={handleWorklistChange} />
        )}
        <div className="lender-lane__toolbar">
          {/* Archived browse is read-only (T13 follow-up 4b) -- no point
              offering selection when bulk assign/forward/complete/archive
              all act on the live email_tasks table, not archived rows. */}
          {!filters.archived && (
            <label className="lender-lane__select-all">
              <input
                type="checkbox"
                aria-label="Select all on this page"
                checked={allOnPageChecked}
                disabled={rows.length === 0}
                onChange={toggleAllOnPage}
              />
              Select all ({rows.length})
            </label>
          )}
          {sortedByReplyBy ? (
            <span className="lender-lane__sort-note">Soonest reply-by date first</span>
          ) : (
            <label className="lender-lane__sort">
              Sort
              <select
                aria-label="Sort"
                value={sortChoice}
                onChange={(e) => setSortChoice(e.target.value as SortChoice)}
              >
                <option value="receivedAsc">Received (oldest first)</option>
                <option value="receivedDesc">Received (newest first)</option>
                <option value="createdDesc">Created (newest first)</option>
              </select>
            </label>
          )}
          {/* Archived view (T13 follow-up 4b's read-only path): same guard
              as `Select all` above, plus a data-trust reason of its own --
              archived_email_tasks only gained a real ai_processed_at
              column on 2026-07-14 (2026-07-14-archived-email-tasks-ai-
              columns.sql), explicitly NOT backfilled (lines 17-19 of that
              migration), so an archived row from before that date reads
              NULL regardless of whether the AI genuinely processed it.
              Offering this filter there would let staff pick "Awaiting AI"
              and get back a page mostly full of rows the AI DID process --
              and since the row badge itself is suppressed on this same
              view for the identical reason (see `aiBadgeUntrustworthy`
              below), no row would carry any marker to contradict the
              filter. Hiding the control (rather than just not sending the
              param) also forces `aiProcessed` back to `undefined` in
              fetchRows below whenever archived is active, so a filter
              chosen on the live view can't silently carry over when the
              user toggles archived on. */}
          {!filters.archived && (
            <label className="lender-lane__sort">
              AI status
              <select
                aria-label="AI status"
                value={aiProcessed}
                onChange={(e) => handleAiProcessedChange(e.target.value as 'all' | 'processed' | 'unprocessed')}
              >
                <option value="all">All</option>
                <option value="processed">AI reviewed</option>
                <option value="unprocessed">Awaiting AI</option>
              </select>
            </label>
          )}
          {workflowSupported && <label className="lender-lane__sort">Workflow
            <select aria-label="Workflow" value={workflowFilter}
              onChange={(event) => handleWorkflowFilter(event.target.value as 'all' | 'only' | 'hide')}>
              <option value="all">All</option><option value="only">With a workflow run</option>
              <option value="hide">Without a workflow run</option>
            </select>
          </label>}
        </div>

        {!filters.archived && checkedIds.size > 0 && (
          <div className="lender-lane__bulk-bar">
            <span className="lender-lane__bulk-count">{checkedIds.size} selected</span>
            <div className="lender-lane__bulk-actions">
              <Button size="sm" variant="secondary" onClick={() => setBulkAssignOpen(true)}>
                Assign claim
              </Button>
              <Button
                size="sm"
                variant="secondary"
                title={bulkForwardEligible ? undefined : 'Select exactly one email to forward.'}
                onClick={handleBulkForwardClick}
              >
                Forward to client
              </Button>
              <Button size="sm" variant="primary" onClick={handleBulkComplete}>
                Complete
              </Button>
              <Button size="sm" variant="secondary" onClick={handleBulkArchiveClick}>
                Archive
              </Button>
              {/* Item 2 (lane-feedback batch 28/07, Ryan decision): "assign to
                  staff" is no longer admin-only -- any authenticated staff
                  member can assign a task to any roster member, matching the
                  backend's POST /ui/team-tasks/bulk-assign, which moved from
                  require_role(['admin']) to require_auth. Was `isAdmin &&`. */}
              <Button size="sm" variant="secondary" onClick={() => setBulkAssignMemberOpen(true)}>
                Assign to staff
              </Button>
            </div>
            {bulkProgress && (
              <span className="lender-lane__bulk-progress">
                {BULK_ACTION_PROGRESS_VERB[bulkProgress.action]} {bulkProgress.done}/{bulkProgress.total}
              </span>
            )}
          </div>
        )}

        {/* Blocked case: every checked row is assigned to a claim, so there's
            nothing archivable -- the modal never opens (see
            `handleBulkArchiveClick`). Cleared the same way `bulkResult` is,
            by any fresh selection or filter/query change. */}
        {archiveBlockedMessage && (
          <div className="lender-lane__archive-blocked" role="status">
            <span>{archiveBlockedMessage}</span>
            <button
              type="button"
              className="lender-lane__bulk-result-dismiss"
              aria-label="Dismiss"
              onClick={() => setArchiveBlockedMessage(null)}
            >
              &times;
            </button>
          </div>
        )}

        {/* Deliberately NOT gated on checkedIds.size > 0 -- handleBulkComplete
            clears checkedIds in the same batch it sets this result, so
            gating the summary on the selection would hide it (incl. a
            partial-failure "N failed") the instant it appears. Persists
            until the staff member dismisses it, checks something new, or the
            filter/query changes (see the effect clearing bulkResult). */}
        {bulkResult && (
          <div className="lender-lane__bulk-result" role="status">
            <span className="lender-lane__bulk-result-message">
              {bulkResult.failed > 0
                ? `${bulkResult.total - bulkResult.failed}/${bulkResult.total} ${BULK_ACTION_RESULT_SUFFIX[bulkResult.action]} — ${bulkResult.failed} failed`
                : `${bulkResult.total} ${BULK_ACTION_RESULT_SUFFIX[bulkResult.action]}`}
            </span>
            <button
              type="button"
              className="lender-lane__bulk-result-dismiss"
              aria-label="Dismiss completion result"
              onClick={() => setBulkResult(null)}
            >
              &times;
            </button>
          </div>
        )}

        {loading && (
          <div className="lane-skeleton-rows" aria-hidden="true">
            <div className="lane-skeleton-row" />
            <div className="lane-skeleton-row" />
            <div className="lane-skeleton-row" />
          </div>
        )}
        {error && <div className="lender-lane__error">{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div className="lender-lane__empty">
            <div className="lender-lane__empty-title">
              {filters.archived ? 'No archived lender emails' : 'Nothing waiting from lenders'}
            </div>
            <div className="lender-lane__empty-body">
              {filters.archived
                ? 'No archived lender emails match this search.'
                : activeWorklist !== 'all'
                  ? `Nothing on the ${activeWorklistLabel} list right now.`
                  : (filters.q || filters.senderEmail || filters.claim || filters.assigned || receivedFromActive)
                    ? 'No lender emails match the current filters.'
                    : 'All lender emails have been actioned.'}
            </div>
          </div>
        )}

        <div className="lender-lane__rows">
          {/* Rows render in fetched order. The open email used to lift to row 1
              inside a sticky card; removed 2026-08-27 (Dec) along with the
              lanePinnedTasks preference. The selected TINT is what marks it now,
              and clicking it again still closes it. */}
          {rows.map((t) => {
            const level = agingLevel(t.received_date);
            const isSelected = slotProps.selectedTaskId === t.id;
            const due = dueText(t);
            const link = claimLink(t);
            const marker = finalResponseMarker(t);
            const rowEl = (
              <div
                data-task-id={t.id}
                className={`lender-lane__row ${isSelected ? 'lender-lane__row--selected' : ''}`}
              >
                {!filters.archived && (
                  <input
                    type="checkbox"
                    className="lender-lane__row-checkbox"
                    aria-label={`Select ${t.subject || '(no subject)'}`}
                    checked={checkedIds.has(t.id)}
                    onChange={() => toggleChecked(t.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <div
                  className="lender-lane__row-body"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSelect(slotProps.selectedTaskId, t.id, slotProps.onSelectTask)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') toggleSelect(slotProps.selectedTaskId, t.id, slotProps.onSelectTask);
                  }}
                >
                  <div className="lender-lane__row-main">
                    <span className="lender-lane__row-sender">{t.email_from || '(unknown sender)'}</span>
                    <span className="lender-lane__row-subject">{renderSubjectWithRef(t.subject)}</span>
                    {/* Lender task lists (spec §4.12): the server's plain reason and the
                        list's date. The note is the backend's own words; the date is a
                        calendar day formatted by hand (worklists.formatDueDate). Lists are
                        worked on Pending only: completed and archived rows keep the columns
                        for reporting but show none of it (final review C9). The reason
                        code is for reporting and never shown (C5). */}
                    {worklistApplies && (t.worklistNote || due) && (
                      <span className="lender-lane__row-note" title={t.worklistNote || undefined}>
                        {t.worklistNote && <span>{t.worklistNote}</span>}
                        {t.worklistNote && due && ' · '}
                        {due && (
                          <span className={`lender-lane__due lender-lane__due--${dueTone(t.worklistDueDate) ?? 'later'}`}>
                            {due}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="lender-lane__row-side">
                    {t.assignedClaimId ? (
                      <>
                        <span className="lender-lane__claim-chip">Claim {t.assignedClaimId}</span>
                        {link?.kind === 'ai' && <span className="lender-lane__ai-attached">Attached by AI</span>}
                      </>
                    ) : link?.kind === 'suggested' && worklistApplies ? (
                      // Spec §4.10 / R15: in Watching nothing is attached; staff see where it would go.
                      <span
                        className="lender-lane__suggest-chip"
                        title="The AI would attach this email to this claim. While the AI is Watching it attaches nothing."
                      >
                        Would attach to {link.claimRef}
                      </span>
                    ) : (
                      <span className="lender-lane__no-claim-flag">No claim</span>
                    )}
                    {showCompletionInfo ? (
                      <span className="lane-completed-info">
                        {filters.archived ? (
                          <>
                            {t.archivedBy && <ActorLabel raw={t.archivedBy} />}
                            {t.archivedAt && <span>{formatShortDate(t.archivedAt)}</span>}
                          </>
                        ) : (
                          <>
                            {t.completedBy && <ActorLabel raw={t.completedBy} />}
                            {t.completedAt && <span>{formatShortDate(t.completedAt)}</span>}
                          </>
                        )}
                      </span>
                    ) : (
                      <span
                        className={`lender-lane__aging lender-lane__aging--${level ?? 'unknown'}`}
                        title={`Waiting ${agingLabel(t.received_date)}`}
                      >
                        {formatReceivedDDMM(t.received_date)}
                      </span>
                    )}
                    {/* Tri-state, not a `? :` on a boolean -- `t.aiProcessed`
                        is `undefined` for any row served by a backend that
                        predates this field, and this repo's deploy split
                        (frontend auto-deploys on merge to main; backend is a
                        separate manual push) guarantees a window where the
                        new frontend runs against the old backend. Treating
                        `undefined` as `false` there would badge EVERY row
                        "Awaiting AI", including ones the AI already
                        processed -- the exact false-negative this feature
                        exists to prevent, reintroduced through deploy
                        ordering. Silence (no badge) is honest; a confident
                        wrong label is not. */}
                    {t.aiProcessed === true && (
                      <span
                        className="lane-ai-status-badge lane-ai-status-badge--done"
                        title="The AI has classified this task"
                      >
                        AI reviewed
                      </span>
                    )}
                    {/* "Awaiting AI" (the false case) is suppressed under
                        `aiBadgeUntrustworthy` (completed sub-tab / archived
                        view) for two independent reasons that happen to
                        share one boolean:
                        - Completed: the tooltip below reads "it may still
                          be auto-completed", which is false for a task
                          that's already completed -- a human closed it, the
                          AI was never going to touch it again.
                        - Archived: archived_email_tasks only gained
                          ai_processed_at on 2026-07-14
                          (2026-07-14-archived-email-tasks-ai-columns.sql),
                          NOT backfilled -- a row archived before that date
                          reads NULL (-> aiProcessed: false) regardless of
                          whether the AI actually processed it beforehand,
                          so "false" isn't trustworthy data there (same
                          reason the AI-status filter control above is
                          hidden entirely on the archived view).
                        The "AI reviewed" (true) case above has neither
                        problem -- it's only ever true when a real
                        ai_processed_at timestamp exists -- so it keeps
                        showing on both those views. */}
                    {/* A row on a list has been decided by the AI, so "Awaiting AI" would be wrong there. */}
                    {t.aiProcessed === false && !aiBadgeUntrustworthy && !t.aiSuspectsFinalResponse && !t.worklist && (
                      <span
                        className="lane-ai-status-badge lane-ai-status-badge--pending"
                        title="The AI has not reached this task yet — it may still be auto-completed"
                      >
                        Awaiting AI
                      </span>
                    )}
                    {/* finalResponseMarker: the server's finalResponseBadge when sent
                        (worklist final_response or a staff-confirmed flag), else -- older
                        backend only -- today's two badges, unchanged. A final response
                        the AI couldn't prove gets "Final response?" (final review C4). */}
                    {marker === 'final' && (
                      <span
                        className="lane-final-response-badge"
                        title="Final response: the FOS referral deadline applies"
                      >
                        Final response
                      </span>
                    )}
                    {marker === 'check' && (
                      <span className="lane-final-response-badge lane-final-response-badge--check" title={FINAL_RESPONSE_CHECK_WORDS}>
                        Final response?
                      </span>
                    )}
                    {marker === 'suspected' && (
                      <span className="lane-ai-status-badge lane-ai-status-badge--pending"
                        title="The AI recorded this assessment in Watching mode. A person needs to review the email; the saved final-response flag has not changed.">
                        AI suspects final response — needs review
                      </span>
                    )}
                    {t.assignedToTeamMember && (
                      <span className="lane-assignee-pill">{t.assignedToTeamMember}</span>
                    )}
                    {t.workflow?.admission_kind === 'example' && <span className="lane-ai-status-badge"
                      title={`${t.workflow.name || 'Workflow'} version ${t.workflow.version}`}>
                      {replacementRunLabel(t.workflow)}
                    </span>}
                  </div>
                </div>
              </div>
            );
            return <React.Fragment key={t.id}>{rowEl}</React.Fragment>;
          })}
        </div>
      </div>
  );

  // Buttons for the open email's actions row (TaskDetailPane `actionsExtra`).
  // The list comes from the loaded row: a deep link to a row this page never
  // fetched shows no list buttons, and staff work it normally. Pending only:
  // completed and archived emails are off the lists (final review C9).
  const renderWorklistActions = (task: UnifiedTask) => {
    const listRow = rowsById[task.id];
    if (!canTeach || !worklistApplies || !listRow) return null;
    // Freshdesk #705 item 1a: on every list, not only Not sure
    // (worklists.canMakeExample says which, and why). It opens the teach form
    // with no final-response setting chosen; the email stays on its list.
    const makeExample = canMakeExample(listRow.worklist) ? (
      <Button size="sm" variant="secondary" onClick={() => setTeach({ row: listRow, stance: null })}>
        Make this an example
      </Button>
    ) : null;
    if (listRow.worklist === 'final_response') {
      // The verdict buttons still come first and offer the teach form with
      // their answer filled in; "Make this an example" is for teaching
      // without recording a verdict.
      return (
        <>
          <Button size="sm" variant="secondary" onClick={() => setFrCheck({ row: listRow, isFinalResponse: true })}>
            Confirm final response
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setFrCheck({ row: listRow, isFinalResponse: false })}>
            Not a final response
          </Button>
          {makeExample}
        </>
      );
    }
    return makeExample;
  };

  const renderDetail = ({ selectedTaskId, showToast }: LaneDetailSlotProps) => (
    <TaskDetailPane
      taskId={selectedTaskId}
      onChanged={() => { fetchRows(); }}
      showToast={showToast}
      emptyMessage="Select an email to view it here."
      renderSubject={(subject) => renderSubjectWithRef(subject)}
      lane="lender"
      // Read off the loaded row: a deep link to a row this page never fetched
      // simply gets no suggestion (the picker behaves as before). Pending only,
      // like the row's own "Would attach to" chip.
      suggestedClaimRef={worklistApplies && selectedTaskId ? rowsById[selectedTaskId]?.aiSuggestedClaimRef ?? null : null}
      actionsExtra={renderWorklistActions}
    />
  );

  return (
    // OneEmailModal reports through the curation toast, which needs this
    // provider; it stays mounted so a "Saved" toast outlives the closed form.
    // The teach form follows the same backend check as AI Curation (final fix
    // wave C1/C2); only people who can teach the AI ever ask it.
    <ToastProvider>
      <FinalResponseSupportProvider eager={canTeach}>
        <LaneShell
          lane="lender"
          renderList={renderList}
          renderDetail={renderDetail}
          renderChips={renderChips}
          totalCount={total}
          statusCounts={statusCounts}
          // Bounded split height so the list and the open email scroll
          // independently and the selected row stays put, as on the Client lane.
          // Covers Completed as well as Pending -- LaneShell renders one split and
          // the sub-tabs only swap the data.
          viewportSplit
        />
        {bulkAssignOpen && (
          <ClaimPickerModal
            title="Assign to a claim"
            hint={`Assign ${checkedIds.size} selected email${checkedIds.size === 1 ? '' : 's'} to:`}
            confirmLabel="Assign"
            processing={bulkAssigning}
            getConfirmation={getBulkAssignConfirmation}
            onConfirm={handleBulkAssignConfirm}
            onClose={() => setBulkAssignOpen(false)}
          />
        )}
        {bulkArchiveOpen && (
          <ArchiveReasonModal
            title="Archive selected emails"
            hint={archiveSkippedCount > 0
              ? `Archive ${archivableIds.length} tasks? ${archiveSkippedCount} of the selected are assigned to a claim and will be skipped.`
              : `Archive ${archivableIds.length} tasks?`}
            confirmLabel="Archive emails"
            processing={bulkArchiving}
            onConfirm={handleBulkArchiveConfirm}
            onClose={() => setBulkArchiveOpen(false)}
          />
        )}
        {bulkForwardTaskId && (
          <ForwardComposer
            taskId={bulkForwardTaskId}
            onClose={() => setBulkForwardTaskId(null)}
            onSent={() => { setBulkForwardTaskId(null); setCheckedIds(new Set()); fetchRows(); }}
          />
        )}
        {bulkAssignMemberOpen && (
          <MemberPickerModal
            title="Assign to a staff member"
            hint={`Assign ${checkedIds.size} selected email${checkedIds.size === 1 ? '' : 's'} to:`}
            confirmLabel="Assign"
            lane="lender"
            processing={bulkAssigningMember}
            onConfirm={handleBulkAssignMemberConfirm}
            onClose={() => setBulkAssignMemberOpen(false)}
          />
        )}
        {frCheck && (
          <FinalResponseCheckModal
            taskId={frCheck.row.id}
            subject={frCheck.row.subject}
            isFinalResponse={frCheck.isFinalResponse}
            onClose={() => setFrCheck(null)}
            onSaved={() => {
              // The verdict changes the email's list: forget the old row so its
              // verdict buttons go at once, before (or without) the refresh (C10).
              const savedId = frCheck.row.id;
              setRowsById((prev) => {
                const next = { ...prev };
                delete next[savedId];
                return next;
              });
              fetchRows();
            }}
            onTeach={(isFinal) => {
              const checkedRow = frCheck.row;
              setFrCheck(null);
              setTeach({ row: checkedRow, stance: isFinal ? 'final_response' : 'not_final_response' });
            }}
          />
        )}
        {teach && (
          <OneEmailModal
            senders={[]}
            preset={teachPreset(teach.row, teach.stance)}
            onClose={() => setTeach(null)}
            onSaved={() => undefined}
          />
        )}
      </FinalResponseSupportProvider>
    </ToastProvider>
  );
};

export default LenderLane;

